import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import PopupOverlay from '../Background/PopupOverlay';
import ConfirmDialog from '../../molecules/ConfirmDialog';
import LayerWizardStepBasics from '../../molecules/Discover/LayerWizardStepBasics';
import LayerWizardStepFilters from '../../molecules/Discover/LayerWizardStepFilters';
import LayerWizardStepPopup from '../../molecules/Discover/LayerWizardStepPopup';
import useDiscoverLayerWizard, {
    WIZARD_STEPS,
    TOTAL_STEPS,
} from '../../../hooks/useDiscoverLayerWizard';
import './DiscoverLayerWizardModal.scss';

/**
 * Discover layer wizard modal with step navigation,
 * progress indicator, and unsaved-changes guard.
 */
export default function DiscoverLayerWizardModal({
    isOpen,
    onClose,
    onCreateLayer = undefined,
    onUpdateLayer = undefined,
    editingLayer = null,
    existingLayerNames = [],
}) {
    const { t } = useTranslation();
    const titleId = useId();
    const descriptionId = `${titleId}-desc`;

    const wizard = useDiscoverLayerWizard();
    const {
        isDirty, reset, loadLayer, setStepValid, stepValidity,
        currentStep, draftPayload, isEditing,
    } = wizard;
    const [showDiscard, setShowDiscard] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    useEffect(() => {
        if (isOpen && editingLayer) {
            loadLayer(editingLayer);
        }
    }, [isOpen, editingLayer, loadLayer]);

    const portalRoot = useMemo(() => {
        if (typeof document === 'undefined') return null;
        return document.getElementById('portal') || document.body;
    }, []);

    /** Guarded close — prompts discard if dirty, otherwise closes directly. */
    const handleRequestClose = useCallback(() => {
        if (isDirty) {
            setShowDiscard(true);
        } else {
            setSaveError(null);
            reset();
            onClose();
        }
    }, [isDirty, reset, onClose]);

    /** User chose "Keep editing" in discard dialog. */
    const handleDiscardCancel = useCallback(() => {
        setShowDiscard(false);
    }, []);

    /** User confirmed discard — reset wizard and close modal. */
    const handleDiscardConfirm = useCallback(() => {
        setShowDiscard(false);
        setSaveError(null);
        reset();
        onClose();
    }, [reset, onClose]);

    /** Step 0 validity callback from LayerWizardStepBasics. */
    const handleBasicsValidityChange = useCallback((valid) => {
        setStepValid(0, valid);
    }, [setStepValid]);

    /** Step 1 validity callback from LayerWizardStepFilters. */
    const handleFiltersValidityChange = useCallback((valid) => {
        setStepValid(1, valid);
    }, [setStepValid]);

    /** Step 2 validity callback from LayerWizardStepPopup. */
    const handlePopupValidityChange = useCallback((valid) => {
        setStepValid(2, valid);
    }, [setStepValid]);

    /** Submit — create or update layer; keep modal open until save succeeds. */
    const handleSubmit = useCallback(async () => {
        if (!stepValidity[currentStep] || saving) return;
        setSaving(true);
        setSaveError(null);
        try {
            if (isEditing) {
                if (typeof onUpdateLayer === 'function') {
                    await onUpdateLayer(draftPayload);
                }
            } else {
                if (typeof onCreateLayer === 'function') {
                    await onCreateLayer(draftPayload);
                }
            }
            reset();
            onClose();
        } catch (err) {
            setSaveError(err?.message || t('discoverPage.layerWizard.errors.saveFailed', 'Failed to save layer. Please try again.'));
        } finally {
            setSaving(false);
        }
    }, [stepValidity, currentStep, draftPayload, isEditing, reset, onClose, onCreateLayer, onUpdateLayer, saving, t]);

    if (!isOpen || !portalRoot) {
        return null;
    }

    return ReactDOM.createPortal(
        <PopupOverlay
            onClose={handleRequestClose}
            closeOnOverlayClick
            closeOnEsc
            zIndex={7100}
        >
            <section
                className="layer-wizard-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                aria-describedby={descriptionId}
            >
                <header className="layer-wizard-modal__header">
                    <h2 id={titleId} className="layer-wizard-modal__title">
                        {wizard.isEditing
                            ? t('discoverPage.layerWizard.titleEdit', 'Edit marker layer')
                            : t('discoverPage.layerWizard.titleCreate', 'Create marker layer')}
                    </h2>
                    <p id={descriptionId} className="layer-wizard-modal__description">
                        {t('discoverPage.layerWizard.description', 'Build a custom layer for Discover filters and popup fields.')}
                    </p>
                </header>

                {/* Step progress indicator */}
                <nav className="layer-wizard-modal__progress" aria-label={t('discoverPage.layerWizard.progress', { current: wizard.currentStep + 1, total: TOTAL_STEPS })}>
                    <ol className="layer-wizard-modal__steps">
                        {WIZARD_STEPS.map((key, idx) => {
                            let modifier = '';
                            if (idx < wizard.currentStep) modifier = ' layer-wizard-modal__step--completed';
                            else if (idx === wizard.currentStep) modifier = ' layer-wizard-modal__step--active';
                            return (
                                <li key={key} className={`layer-wizard-modal__step${modifier}`}>
                                    <span className="layer-wizard-modal__step-number">{idx + 1}</span>
                                    <span className="layer-wizard-modal__step-label">
                                        {t(`discoverPage.layerWizard.steps.${key}`, key)}
                                    </span>
                                </li>
                            );
                        })}
                    </ol>
                </nav>

                <div className="layer-wizard-modal__body" aria-live="polite">
                    {wizard.currentStep === 0 && (
                        <LayerWizardStepBasics
                            name={wizard.name}
                            objectType={wizard.objectType}
                            onNameChange={wizard.setName}
                            onObjectTypeChange={wizard.setObjectType}
                            onValidityChange={handleBasicsValidityChange}
                            existingLayerNames={existingLayerNames}
                            isEditing={wizard.isEditing}
                        />
                    )}
                    {wizard.currentStep === 1 && (
                        <LayerWizardStepFilters
                            objectType={wizard.objectType}
                            matchMode={wizard.matchMode}
                            rules={wizard.rules}
                            ruleErrors={wizard.ruleErrors}
                            prunedRuleCount={wizard.prunedRuleCount}
                            onMatchModeChange={wizard.setMatchMode}
                            onAddRule={wizard.addRule}
                            onUpdateRule={wizard.updateRule}
                            onRemoveRule={wizard.removeRule}
                            onValidityChange={handleFiltersValidityChange}
                            onDismissPruneNotice={wizard.dismissPruneNotice}
                        />
                    )}
                    {wizard.currentStep === 2 && (
                        <LayerWizardStepPopup
                            objectType={wizard.objectType}
                            popupFields={wizard.popupFields}
                            popupHideEmpty={wizard.popupHideEmpty}
                            onToggleField={wizard.togglePopupField}
                            onMoveField={wizard.movePopupField}
                            onHideEmptyChange={wizard.setPopupHideEmpty}
                            onValidityChange={handlePopupValidityChange}
                        />
                    )}
                </div>

                {saveError && (
                    <p className="layer-wizard-modal__error" role="alert">
                        {saveError}
                    </p>
                )}

                <footer className="layer-wizard-modal__footer">
                    <button
                        type="button"
                        className="layer-wizard-modal__cancel"
                        onClick={handleRequestClose}
                        disabled={saving}
                    >
                        {t('discoverPage.layerWizard.actions.cancel', 'Cancel')}
                    </button>

                    <div className="layer-wizard-modal__nav">
                        {!wizard.isFirstStep && (
                            <button
                                type="button"
                                className="layer-wizard-modal__prev"
                                onClick={wizard.goBack}
                                disabled={saving}
                            >
                                {t('discoverPage.layerWizard.actions.previous', 'Previous')}
                            </button>
                        )}

                        {wizard.isLastStep ? (
                            <button
                                type="button"
                                className="layer-wizard-modal__submit"
                                disabled={!wizard.stepValidity[wizard.currentStep] || saving}
                                onClick={handleSubmit}
                            >
                                {saving
                                    ? t('discoverPage.layerWizard.actions.saving', 'Saving…')
                                    : wizard.isEditing
                                        ? t('discoverPage.layerWizard.actions.save', 'Save layer')
                                        : t('discoverPage.layerWizard.actions.create', 'Create layer')}
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="layer-wizard-modal__next"
                                disabled={!wizard.canGoNext}
                                onClick={wizard.goNext}
                            >
                                {t('discoverPage.layerWizard.actions.next', 'Next')}
                            </button>
                        )}
                    </div>
                </footer>
            </section>

            {/* Discard confirmation — rendered inside portal overlay */}
            <ConfirmDialog
                isOpen={showDiscard}
                title={t('discoverPage.layerWizard.discard.title', 'Discard changes?')}
                message={t('discoverPage.layerWizard.discard.message', 'You have unsaved changes in this layer draft.')}
                confirmLabel={t('discoverPage.layerWizard.discard.confirm', 'Discard')}
                cancelLabel={t('discoverPage.layerWizard.discard.stay', 'Keep editing')}
                onConfirm={handleDiscardConfirm}
                onCancel={handleDiscardCancel}
                confirmVariant="danger"
            />
        </PopupOverlay>,
        portalRoot,
    );
}

DiscoverLayerWizardModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onCreateLayer: PropTypes.func,
    onUpdateLayer: PropTypes.func,
    editingLayer: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        objectType: PropTypes.string,
        filters: PropTypes.object,
        popup: PropTypes.object,
    }),
    existingLayerNames: PropTypes.arrayOf(PropTypes.string),
};
